import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FileSpreadsheet, FileText, RefreshCw } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { isoDate } from "../../../utils/attendance";
import {
  SHEETS,
  downloadCsv,
  downloadWorkbook,
  hours,
  rowsFor,
} from "./reportExport";

// The attendance report, on screen, as the table it will be exported as.
//
// Previously this lived behind a dialog that went straight to a download, so
// there was no way to check the numbers before sending them to payroll. The
// table below is built from the same rows the file is, column for column --
// what you review is exactly what you get.

const PRESETS = [
  { label: "Last 7 days", from: () => isoDate(6), to: () => isoDate(0) },
  { label: "Last 30 days", from: () => isoDate(29), to: () => isoDate(0) },
  {
    label: "This month",
    from: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("en-CA");
    },
    to: () => isoDate(0),
  },
  {
    label: "Last month",
    from: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth() - 1, 1).toLocaleDateString("en-CA");
    },
    to: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth(), 0).toLocaleDateString("en-CA");
    },
  },
];

// Columns whose values are numbers and should sit right-aligned.
const isNumericColumn = (key) =>
  /\((h|min)\)$/.test(key) || ["Shifts", "Breaks", "Days Present", "Late Days", "Auto Clock-Outs"].includes(key);

export default function AttendanceReport() {
  const [from, setFrom] = useState(isoDate(29));
  const [to, setTo] = useState(isoDate(0));
  const [agentId, setAgentId] = useState("");
  const [agents, setAgents] = useState([]);

  const [report, setReport] = useState(null);
  const [sheet, setSheet] = useState("summary");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // The report itself is fetched whole; this paginates the on-screen view of it
  // so a 5,000-row detail sheet does not try to render at once.
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  useEffect(() => {
    axiosInstance
      .get(apiEndPointsConfig.agents, { params: { brief: true } })
      .then(({ data }) => setAgents(data?.data || []))
      .catch(() => setAgents([]));
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(apiEndPointsConfig.attendanceReport, {
        params: { from, to, agentId: agentId || undefined },
      });
      setReport(data);
      setPage(0);
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.message || "Could not build the report");
    } finally {
      setLoading(false);
    }
  }, [from, to, agentId]);

  useEffect(() => {
    run();
  }, [run]);

  const rows = useMemo(() => rowsFor(report, sheet), [report, sheet]);
  const columns = rows.length ? Object.keys(rows[0]) : [];
  const visible = useMemo(
    () => rows.slice(page * perPage, page * perPage + perPage),
    [rows, page, perPage]
  );

  useEffect(() => {
    setPage(0);
  }, [sheet]);

  const agentLabel = agentId
    ? agents.find((a) => a.id === agentId)?.name || agentId
    : "All agents";

  const t = report?.totals;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* ── Controls ─────────────────────────────────────────────────────── */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.label}
              label={p.label}
              size="small"
              onClick={() => {
                setFrom(p.from());
                setTo(p.to());
              }}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            type="date"
            size="small"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            type="date"
            size="small"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
          <TextField
            select
            size="small"
            label="Agent"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value="">All agents</MenuItem>
            {agents.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.name || a.userid}
              </MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            startIcon={<RefreshCw size={15} />}
            onClick={run}
            disabled={loading}
            sx={{ color: "#0C237B", textTransform: "none", fontWeight: 600 }}
          >
            Refresh
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            startIcon={<FileSpreadsheet size={17} />}
            disabled={!rows.length || loading}
            onClick={() => downloadWorkbook(report, agentLabel)}
            sx={{
              bgcolor: "#0C237B",
              "&:hover": { bgcolor: "#1a3399" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Excel (all sheets)
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileText size={17} />}
            disabled={!rows.length || loading}
            onClick={() => downloadCsv(report, sheet)}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              borderColor: "#0C237B",
              color: "#0C237B",
            }}
          >
            CSV (this table)
          </Button>
        </Box>
      </Paper>

      {/* ── Headline totals ──────────────────────────────────────────────── */}
      {t && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(5, minmax(0,1fr))" },
            gap: 2,
            mb: 2,
          }}
        >
          {[
            { label: "Agents", value: t.agents },
            { label: "Shifts", value: t.shifts },
            { label: "Days Present", value: t.daysPresent },
            { label: "Productive Hours", value: hours(t.productiveMs) },
            { label: "Late Arrivals", value: t.lateCount, danger: t.lateCount > 0 },
          ].map((s) => (
            <Paper
              key={s.label}
              sx={{ p: 2, borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none" }}
            >
              <Typography variant="caption" sx={{ color: "#6b7280" }}>
                {s.label}
              </Typography>
              <Typography
                sx={{ fontSize: 22, fontWeight: 700, color: s.danger ? "#dc2626" : "#111827", lineHeight: 1.2 }}
              >
                {s.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}

      {/* ── The report, as a table ───────────────────────────────────────── */}
      <Paper sx={{ borderRadius: "12px", border: "1px solid #e5e7eb", boxShadow: "none", overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            p: 2,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sheet}
            onChange={(e, v) => v && setSheet(v)}
          >
            {Object.values(SHEETS).map((s) => (
              <ToggleButton key={s.key} value={s.key} sx={{ textTransform: "none" }}>
                {s.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            {SHEETS[sheet].hint}
            {report ? ` · ${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"}` : ""}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !rows.length ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
              No attendance recorded between {from} and {to}.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 560 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell
                        key={c}
                        align={isNumericColumn(c) ? "right" : "left"}
                        sx={{
                          fontWeight: 700,
                          color: "#6b7280",
                          bgcolor: "#f9fafb",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((r, i) => (
                    // Row identity is positional: these are derived report
                    // rows, not records with ids of their own.
                    <TableRow key={`${page}-${i}`} hover>
                      {columns.map((c) => (
                        <TableCell
                          key={c}
                          align={isNumericColumn(c) ? "right" : "left"}
                          sx={{
                            whiteSpace: c === "Break Detail" || c === "Note" ? "normal" : "nowrap",
                            maxWidth: c === "Break Detail" || c === "Note" ? 260 : undefined,
                            fontWeight: c === "Agent" ? 600 : 400,
                            color: c === "Productive (h)" ? "#059669" : "#374151",
                          }}
                        >
                          {r[c] === "" || r[c] === null || r[c] === undefined ? "—" : String(r[c])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 250]}
            />
          </>
        )}
      </Paper>

      {report && (
        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#9ca3af" }}>
          Generated {new Date(report.generatedAt).toLocaleString()} · {report.timezone} · hours are
          decimal (7.5 = 7h 30m)
        </Typography>
      )}
    </Box>
  );
}
